// frontend/src/components/widgets/StreamingWidget.js - OPTIMIZED WITH RESPONSIVE + THEMES
import React, { useState, useEffect, useRef } from 'react';
import { Video, Wifi, WifiOff, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const StreamingWidget = ({ config, screenSize = 'FHD' }) => {
  const { t } = useTranslation();
  const [isLive, setIsLive] = useState(null);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);
  const twitchPlayerRef = useRef(null);
  const [twitchPlayer, setTwitchPlayer] = useState(null);
  const [twitchScriptLoaded, setTwitchScriptLoaded] = useState(false);

  // ✅ RESPONSIVE: Get multipliers based on screen size
  const getResponsiveMultipliers = () => {
    const multipliers = {
      SM: { font: 0.4, spacing: 0.45, icon: 0.5 },    // 640px - Mobile
      MD: { font: 0.6, spacing: 0.65, icon: 0.7 },    // 768px - Tablet
      HD: { font: 0.8, spacing: 0.85, icon: 0.85 },   // 1024px - HD
      FHD: { font: 1.0, spacing: 1.0, icon: 1.0 },    // 1920px - Full HD
      QHD: { font: 1.15, spacing: 1.1, icon: 1.1 },   // 2560px - 2K
      '4K': { font: 1.4, spacing: 1.3, icon: 1.3 }    // 3840px+ - 4K
    };
    return multipliers[screenSize] || multipliers.FHD;
  };

  const m = getResponsiveMultipliers();

  // ✅ GET THEME STYLES
  const getThemeStyles = () => {
    const themes = {
      dark: {
        background: 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900',
        overlayBg: 'bg-black/70',
        liveButton: 'bg-red-600',
        text: 'text-white',
        textSecondary: 'text-gray-300'
      },
      blue: {
        background: 'bg-gradient-to-br from-[#3b82f6] via-[#2563eb] to-[#1d4ed8]',
        overlayBg: 'bg-blue-900/80',
        liveButton: 'bg-red-600',
        text: 'text-white',
        textSecondary: 'text-blue-100'
      },
      classicBlue: {
        background: 'bg-gradient-to-br from-[#1e3c72] via-[#2a5298] to-[#1e3c72]',
        overlayBg: 'bg-blue-900/80',
        liveButton: 'bg-red-600',
        text: 'text-white',
        textSecondary: 'text-blue-100'
      },
      purple: {
        background: 'bg-gradient-to-br from-purple-900 to-indigo-900',
        overlayBg: 'bg-purple-900/80',
        liveButton: 'bg-red-600',
        text: 'text-white',
        textSecondary: 'text-purple-100'
      },
      gaming: {
        background: 'bg-gradient-to-br from-green-900 via-emerald-800 to-teal-900',
        overlayBg: 'bg-green-900/80',
        liveButton: 'bg-red-600',
        text: 'text-white',
        textSecondary: 'text-green-100'
      },
      minimal: {
        background: 'bg-black',
        overlayBg: 'bg-gray-900/80',
        liveButton: 'bg-red-600',
        text: 'text-white',
        textSecondary: 'text-gray-400'
      }
    };
    return themes[config.theme || 'dark'];
  };

  const theme = getThemeStyles();

  useEffect(() => {
    checkStreamStatus();
    const interval = setInterval(checkStreamStatus, (config.checkInterval || 300) * 1000);
    return () => clearInterval(interval);
  }, [config]);

  // Load Twitch Player SDK
  useEffect(() => {
    if (config.platform === 'twitch' && !twitchScriptLoaded) {
      const script = document.createElement('script');
      script.src = 'https://player.twitch.tv/js/embed/v1.js';
      script.async = true;
      script.onload = () => {
        console.log('Twitch Player SDK loaded');
        setTwitchScriptLoaded(true);
      };
      script.onerror = () => {
        console.error('Failed to load Twitch Player SDK');
      };
      document.body.appendChild(script);

      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    }
  }, [config.platform, twitchScriptLoaded]);

  // Initialize Twitch Player
  useEffect(() => {
    if (config.platform === 'twitch' && twitchScriptLoaded && window.Twitch && twitchPlayerRef.current && !twitchPlayer) {
      try {
        const player = new window.Twitch.Player(twitchPlayerRef.current, {
          channel: config.channelId,
          width: '100%',
          height: '100%',
          autoplay: false,
          muted: config.muted,
          parent: ['app.alpenscreen.ch', 'www.alpenscreen.ch', 'localhost']
        });

        player.addEventListener(window.Twitch.Player.READY, () => {
          console.log('Twitch Player ready');
          setTwitchPlayer(player);

          if (config.autoplay) {
            const playButton = document.createElement('button');
            playButton.style.position = 'absolute';
            playButton.style.opacity = '0';
            playButton.style.pointerEvents = 'none';
            document.body.appendChild(playButton);
            
            playButton.click();
            
            setTimeout(() => {
              try {
                const playPromise = player.play();
                if (playPromise && typeof playPromise.then === 'function') {
                  playPromise
                    .then(() => {
                      console.log('Twitch autoplay started');
                      if (!config.muted) {
                        setTimeout(() => player.setMuted(false), 2000);
                      }
                    })
                    .catch(err => console.log('Twitch autoplay failed:', err));
                } else {
                  console.log('Twitch player play() did not return a Promise');
                  if (!config.muted) {
                    setTimeout(() => player.setMuted(false), 2000);
                  }
                }
              } catch (err) {
                console.log('Twitch play error:', err);
              }
            }, 500);
            
            setTimeout(() => {
              if (document.body.contains(playButton)) {
                document.body.removeChild(playButton);
              }
            }, 1000);
          }
        });

        player.addEventListener(window.Twitch.Player.PLAY, () => {
          console.log('Twitch player started playing');
        });

      } catch (err) {
        console.error('Error initializing Twitch player:', err);
      }
    }
  }, [config.platform, twitchScriptLoaded, twitchPlayerRef.current, config.channelId, config.autoplay, config.muted]);

  // YouTube Autoplay
  useEffect(() => {
    if (iframeRef.current && config.autoplay && !config.muted && config.platform === 'youtube') {
      setTimeout(() => {
        try {
          iframeRef.current.contentWindow.postMessage(
            '{"event":"command","func":"unMute","args":""}',
            '*'
          );
        } catch (e) {
          console.log('YouTube unmute failed:', e);
        }
      }, 3000);
    }
  }, [config.autoplay, config.muted, config.platform, isLive]);

  const checkStreamStatus = async () => {
    try {
      setIsLive(true);
      setError(null);
    } catch (err) {
      console.error('Error checking stream status:', err);
      setError(err.message);
      setIsLive(false);
    }
  };

  const getYouTubeEmbedUrl = () => {
    const { channelId, autoplay, muted } = config;
    const baseUrl = 'https://www.youtube.com/embed/' + channelId;
    const params = new URLSearchParams({
      autoplay: autoplay ? '1' : '0',
      mute: (muted || autoplay) ? '1' : '0',
      enablejsapi: '1',
      modestbranding: '1',
      rel: '0'
    });
    return `${baseUrl}?${params.toString()}`;
  };

  const getTwitchChatUrl = () => {
    if (config.platform !== 'twitch' || !config.showChat) return null;
    return `https://www.twitch.tv/embed/${config.channelId}/chat?parent=app.alpenscreen.ch&parent=www.alpenscreen.ch&parent=localhost`;
  };

  // ✅ OFFLINE FALLBACK - RESPONSIVE
  if (isLive === false) {
    if (config.fallbackBehavior === 'skip') {
      return null;
    }

    if (config.fallbackBehavior === 'show_message') {
      return (
        <div className={`w-full h-full flex items-center justify-center ${theme.background} ${theme.text}`}>
          <div className="text-center" style={{ padding: `${64 * m.spacing}px` }}>
            <WifiOff 
              size={80 * m.icon} 
              className="mx-auto opacity-50"
              style={{ marginBottom: `${48 * m.spacing}px` }}
            />
            <h2 
              className="font-bold"
              style={{ 
                fontSize: `${64 * m.font}px`,
                marginBottom: `${32 * m.spacing}px`
              }}
            >
              Stream Offline
            </h2>
            <p 
              className={theme.textSecondary}
              style={{ fontSize: `${32 * m.font}px` }}
            >
              {config.platform === 'youtube' ? 'YouTube' : 'Twitch'} channel is currently offline
            </p>
            <p 
              className="opacity-50"
              style={{ 
                fontSize: `${24 * m.font}px`,
                marginTop: `${32 * m.spacing}px`
              }}
            >
              {config.channelId}
            </p>
          </div>
        </div>
      );
    }

    if (config.fallbackBehavior === 'show_logo') {
      return (
        <div className={`w-full h-full flex items-center justify-center ${theme.background} ${theme.text}`}>
          <div className="text-center" style={{ padding: `${64 * m.spacing}px` }}>
            <Video 
              size={120 * m.icon}
              style={{ 
                margin: '0 auto',
                marginBottom: `${48 * m.spacing}px`
              }}
            />
            <h2 
              className="font-bold"
              style={{ 
                fontSize: `${80 * m.font}px`,
                marginBottom: `${32 * m.spacing}px`
              }}
            >
              {config.channelId}
            </h2>
            <p 
              className={theme.textSecondary}
              style={{ fontSize: `${32 * m.font}px` }}
            >
              Stream will start soon
            </p>
          </div>
        </div>
      );
    }
  }

  // ✅ LOADING STATE - RESPONSIVE
  if (isLive === null) {
    return (
      <div className={`w-full h-full flex items-center justify-center ${theme.background} ${theme.text}`}>
        <div className="text-center">
          <Loader 
            size={60 * m.icon} 
            className="mx-auto animate-spin"
            style={{ marginBottom: `${32 * m.spacing}px` }}
          />
          <p style={{ fontSize: `${32 * m.font}px` }}>
            Checking stream status...
          </p>
        </div>
      </div>
    );
  }

  // ✅ ERROR STATE - RESPONSIVE
  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-red-900 text-white">
        <div className="text-center" style={{ padding: `${64 * m.spacing}px` }}>
          <WifiOff 
            size={60 * m.icon}
            style={{ 
              margin: '0 auto',
              marginBottom: `${32 * m.spacing}px`
            }}
          />
          <h2 
            className="font-bold"
            style={{ 
              fontSize: `${48 * m.font}px`,
              marginBottom: `${16 * m.spacing}px`
            }}
          >
            Error
          </h2>
          <p style={{ fontSize: `${24 * m.font}px` }}>{error}</p>
        </div>
      </div>
    );
  }

  const chatUrl = getTwitchChatUrl();

  // ✅ LIVE STREAM DISPLAY - RESPONSIVE
  return (
    <div className="w-full h-full relative bg-black">
      {/* Live Indicator - RESPONSIVE */}
      <div 
        className={`absolute z-10 ${theme.liveButton} rounded-full flex items-center shadow-lg`}
        style={{
          top: `${32 * m.spacing}px`,
          right: `${32 * m.spacing}px`,
          gap: `${16 * m.spacing}px`,
          padding: `${16 * m.spacing}px ${32 * m.spacing}px`
        }}
      >
        <Wifi size={20 * m.icon} className="animate-pulse" />
        <span 
          className="font-bold text-white"
          style={{ fontSize: `${20 * m.font}px` }}
        >
          LIVE
        </span>
      </div>

      {/* Main Content */}
      <div className="w-full h-full flex">
        {/* Stream Player */}
        <div className={chatUrl ? "w-3/4" : "w-full"} style={{ height: '100%' }}>
          {config.platform === 'youtube' ? (
            <iframe
              ref={iframeRef}
              src={getYouTubeEmbedUrl()}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              className="w-full h-full"
              style={{ visibility: 'visible', display: 'block' }}
              title="YouTube stream"
            />
          ) : (
            <div 
              ref={twitchPlayerRef} 
              id="twitch-player" 
              className="w-full h-full"
              style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                visibility: 'visible',
                display: 'block',
                overflow: 'visible',
                zIndex: 1
              }}
            />
          )}
        </div>

        {/* Twitch Chat */}
        {chatUrl && (
          <div className="w-1/4 h-full bg-gray-800">
            <iframe
              src={chatUrl}
              frameBorder="0"
              className="w-full h-full"
              title="Twitch Chat"
            />
          </div>
        )}
      </div>

      {/* Channel Info Overlay - RESPONSIVE */}
      <div 
        className={`absolute ${theme.overlayBg} backdrop-blur-sm rounded-lg ${theme.text}`}
        style={{
          bottom: `${32 * m.spacing}px`,
          left: `${32 * m.spacing}px`,
          padding: `${24 * m.spacing}px ${48 * m.spacing}px`
        }}
      >
        <div 
          className="flex items-center"
          style={{ gap: `${24 * m.spacing}px` }}
        >
          <Video size={24 * m.icon} />
          <div>
            <div 
              className="opacity-75 capitalize"
              style={{ fontSize: `${14 * m.font}px` }}
            >
              {config.platform}
            </div>
            <div 
              className="font-semibold"
              style={{ fontSize: `${18 * m.font}px` }}
            >
              {config.channelId}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StreamingWidget;